import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject, forkJoin, throwError, from } from 'rxjs';
import { Visitor } from '../models/visitor';
import { isNullOrUndefined } from 'util';
import { mergeMap, delay, map, retry, catchError } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { AuthService } from './auth.service';

export const enum VisitorDataResolution {
  YearCompare,
  DayCompare,
  Day,
  Month,
  Year
}

export class VisitorResponse {
  name: string;
  values: Visitor[];
}

export const enum Type
{
  Processor,
  Gate,
  Entrance,
  Store,
  Group
}

@Injectable({
  providedIn: 'root'
})
export class VisitorService {

  private visitors = new BehaviorSubject<VisitorResponse[]>([]);
  private visitorCount = new BehaviorSubject<number>(0);
  private prevVisitorDataResolution: VisitorDataResolution = null;
  private prevFromDate: Date = null;
  private prevToDate: Date = null;

  constructor(private http: HttpClient, private authService: AuthService) { }

  
  setDayCompareDateSelection(fromDate1: Date, toDate1: Date, fromDate2: Date, toDate2: Date, storeGuid) {
    let responses: VisitorResponse[] = [];

    return this.getVisitors(fromDate2, toDate2, VisitorDataResolution.DayCompare, storeGuid).pipe(
      map((visitors:any) => {
        let visitorResponse = new VisitorResponse();
        visitorResponse.name = "Visitor Count 1";
        visitorResponse.values = visitors;
        responses.push(visitorResponse);
      }),
      mergeMap(() => { 
        return this.getVisitors(fromDate1, toDate1, VisitorDataResolution.DayCompare, storeGuid)
      }),
      map((visitors: any) => {
        let visitorResponse = new VisitorResponse();
        visitorResponse.name = "Visitor Count 2";
        visitorResponse.values = visitors;
        responses.push(visitorResponse);

        this.visitors.next(responses);
      })
    );
  }

  // For regular date selection (Day, Month, Year)
  setDateSelection(fromDate: Date, toDate: Date, visitorDataResolution: VisitorDataResolution, storeGuid) {
    // Don't request data for the same dates if it's already stored
    if(!isNullOrUndefined(this.prevFromDate) && !isNullOrUndefined(this.prevToDate) && !isNullOrUndefined(this.prevVisitorDataResolution)
        && this.prevFromDate == fromDate && this.prevToDate == toDate && this.prevVisitorDataResolution == visitorDataResolution) {
      return;
    }

    // Send update to all observers 
    return this.getVisitors(fromDate, toDate, visitorDataResolution, storeGuid).pipe(
      map((visitors:any) => {
        let responses: VisitorResponse[] = [];
        let visitorResponse = new VisitorResponse();
        visitorResponse.name = "Visitor Count";
        visitorResponse.values = visitors;
        responses.push(visitorResponse);
        // Update the subject with new visitors data
        this.visitors.next(responses);
      })
    );
  }

  getHourlyActivity(fromDate: any, toDate: any, storeGuid) {
    return from(this.authService.GetDefaultHeaders()).pipe(
      mergeMap((contentHeaders: HttpHeaders) => {
        let hourlyApiUrl = ConfigService.STATS_API_ENDPOINT + "peoplecount/hourly-v1";
        var params = new HttpParams().set('fromDate', fromDate.format('YYYY-MM-DD'))
                                     .set('toDate', toDate.format('YYYY-MM-DD'))
                                     .set('storeGuid', storeGuid);
        return this.http.get<Visitor[]>(hourlyApiUrl, { params: params, headers: contentHeaders })
      }),
      retry(3), // retry a failed request up to 3 times
      catchError(this.handleError), // then handle the error
      map(response => {
        return response['data'];
      })
    );
  }

  getHourlyGenderActivity(fromDate: any, toDate: any, storeGuid: string) {
    return from(this.authService.GetDefaultHeaders()).pipe(
      mergeMap((contentHeaders: HttpHeaders) => {
        let hourlyApiUrl = ConfigService.STATS_API_ENDPOINT + "gendercount/hourly-v1";
        var params = new HttpParams().set('fromDate', fromDate.format('YYYY-MM-DD'))
                                     .set('toDate', toDate.format('YYYY-MM-DD'))
                                     .set('storeGuid', storeGuid);
        return this.http.get<Visitor[]>(hourlyApiUrl, { params: params, headers: contentHeaders })
      }),
      retry(3), // retry a failed request up to 3 times
      catchError(this.handleError), // then handle the error
      map(response => {
        return response['data'];
      })
    );
  }

  getVisitorSubject() : BehaviorSubject<VisitorResponse[]> {
    return this.visitors;
  }

  getVisitorsForTodaySubject() : BehaviorSubject<number> {
    return this.visitorCount;
  }

  private getVisitors(fromDate: Date, toDate: Date, visitorDataResolution: VisitorDataResolution, storeGuid: string): Observable<Visitor[]> {
    let visitors: Visitor[] = [];
    return from(this.authService.GetDefaultHeaders()).pipe(
      mergeMap((contentHeaders: HttpHeaders) => {
        let peopleApiUrl = ConfigService.STATS_API_ENDPOINT + "peoplecount/v1";
        var params = new HttpParams().set('fromDate', fromDate.toISOString())
                                     .set('toDate', toDate.toISOString())
                                     .set('resolution', visitorDataResolution.toString())
                                     .set('storeGuid', storeGuid);
        return this.http.get<Visitor[]>(peopleApiUrl, { params: params, headers: contentHeaders })
      }),
      retry(3), // retry a failed request up to 3 times
      catchError(this.handleError), // then handle the error
      map(response => {
        return response['objectdata'];
      })
    );
  }

  public getVisitorsForToday(storeGuid: string, currentDay: Date): Observable<number> {
    return from(this.authService.GetDefaultHeaders()).pipe(
      mergeMap((contentHeaders: HttpHeaders) => {
        let peopleApiUrl = ConfigService.STATS_API_ENDPOINT + "peoplecount/today";
        var params = new HttpParams().set('storeGuid', storeGuid).set('currentDay', currentDay.toISOString());
        return this.http.get<number>(peopleApiUrl, { params: params, headers: contentHeaders })
      }),
      retry(3),
      catchError(this.handleError),
      map(response => {
        this.visitorCount.next(response['count'])
        return response['count'];
      })
    )
  }

  public getGenderCounts(storeGuid: string, currentDay: Date, fromDate: Date, toDate: Date): Observable<any> {
    return from(this.authService.GetDefaultHeaders()).pipe(
      mergeMap((contentHeaders: HttpHeaders) => {
        let genderApiUrl = ConfigService.STATS_API_ENDPOINT + "gendercount";
        var params = new HttpParams().set('storeGuid', storeGuid)
                                     .set('currentDay', currentDay.toISOString())
                                     .set('fromDate', fromDate.toISOString())
                                     .set('toDate', toDate.toISOString());
        return this.http.get<any>(genderApiUrl, { params: params, headers: contentHeaders })
      }),
      retry(3),
      catchError(this.handleError),
      map(response => {
        return response;
      })
    )
  }

  private handleError(error: HttpErrorResponse) {
		if (error.error instanceof ErrorEvent) {
		  // A client-side or network error occurred. Handle it accordingly.
		  console.error('An error occurred:', error.error.message);
		} else {
		  // The backend returned an unsuccessful response code.
		  // The response body may contain clues as to what went wrong,
		  console.error(
			`Backend returned code ${error.status}, ` +
			`body was: ${error.error}`);
		}
		// return an observable with a user-facing error message
		return throwError(
		  'Unable to fetch visitor counts; please try again later.');
	};
}
